import type { MouseEvent } from 'react';
import type { Media } from '../../../lib/flotiq-client/types/api-response';

import { useTranslation } from 'react-i18next';

import { getMediaUrl } from '../../../lib/flotiq-client/api-helpers';

import { useContentObject } from '../../../hooks/api';

import { defaultButtonsProps, useModals } from '../../../contexts/ModalContext';

import { ArrowUpRightIcon } from '../../../images/shapes';

import LinkButton from '../../LinkButton/LinkButton';

const HOOK_OPTIONS = {
  staleTime: 10 * 60 * 1000, // 10 minutes
  gcTime: 15 * 60 * 1000, // 15 minutes
};

const MediaRender = ({
  media,
  fullSize = false,
}: {
  media: Media;
  fullSize?: boolean;
}) => {
  if (media.type === 'file') {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div
          className={
            'flex items-center justify-center rounded-full bg-blue text-white font-medium ' +
            (fullSize ? 'text-base h-12 w-12' : 'h-10 w-10 text-sm')
          }
        >
          {media.extension || ''}
        </div>
      </div>
    );
  } else {
    return (
      <img
        src={getMediaUrl(media, 0, fullSize ? 0 : 40)}
        alt={media?.alt || media?.id}
        title={media?.title}
        className={
          fullSize
            ? 'mx-auto max-h-[calc(100vh-16rem)] max-w-full object-contain'
            : 'max-h-10 rounded-md'
        }
      />
    );
  }
};

const SingleMediaCellRender = ({ dataUrl }: { dataUrl: string }) => {
  const { t } = useTranslation();
  const modal = useModals();
  const id = dataUrl.split('/').pop();

  const { entity: media, isLoading } = useContentObject(
    '_media',
    id,
    HOOK_OPTIONS,
  );

  const openMediaModal = (e: MouseEvent<HTMLButtonElement>) => {
    if (!media) return;

    e.stopPropagation();
    e.preventDefault();

    modal({
      content: (
        <div className="relative flex flex-col sm:px-6 md:px-4">
          <MediaRender media={media as Media} fullSize />
          <LinkButton
            buttonColor="borderless"
            iconImage={<ArrowUpRightIcon className="w-2.5 text-blue" />}
            additionalClasses="font-bold mt-2 mx-auto"
            target="_blank"
            rel="noreferrer"
            link={getMediaUrl(media, 0, 0, false)}
          >
            {media.fileName}
          </LinkButton>
        </div>
      ),
      buttons: [{ ...defaultButtonsProps.cancel, label: t('Global.Close') }],
      size: 'lg',
    });
  };

  if (isLoading || !media) {
    return null;
  }

  return (
    <button className="cursor-pointer" onClick={openMediaModal}>
      <MediaRender media={media as Media} />
    </button>
  );
};

export default SingleMediaCellRender;
